const PRODUCT_ADDED_EVENT = 'Product Added';
const CHECKOUT_STARTED_EVENT = 'Checkout Started';
const CHECKOUT_DEDUPLICATION_WINDOW_MS = 2000;

let lastCheckoutCapture = {
  fingerprint: null,
  timestamp: 0,
};

function compact(object) {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => {
      if (value == null) return false;
      if (typeof value === 'number' && !Number.isFinite(value)) return false;
      return true;
    }),
  );
}

function toInteger(value, fallback = 0) {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function centsToAmount(value) {
  const cents = Number(value);
  if (!Number.isFinite(cents)) return undefined;

  return Number((cents / 100).toFixed(2));
}

function getCurrency(fallback) {
  if (fallback) return fallback;
  if (typeof Theme !== 'undefined' && Theme.currency) return Theme.currency;
  return 'EUR';
}

function capture(eventName, properties, options) {
  if (typeof window.posthog?.capture !== 'function') return;

  window.posthog.capture(eventName, compact(properties), options);
}

function isGift(item) {
  return item?.properties?._is_gift === true || item?.properties?._is_gift === 'true';
}

function getLineValueInCents(item) {
  const lineValue = item?.final_line_price ?? item?.original_line_price ?? item?.line_price;
  if (Number.isFinite(Number(lineValue))) return Number(lineValue);

  const unitValue = item?.final_price ?? item?.discounted_price ?? item?.price;
  if (!Number.isFinite(Number(unitValue))) return undefined;

  return Number(unitValue) * toInteger(item?.quantity, 1);
}

function normalizeItem(item) {
  const quantity = toInteger(item?.quantity, 1);
  const variantId = item?.variant_id ?? item?.id;
  const productName = item?.product_title ?? item?.title;

  return compact({
    product_id: item?.product_id != null ? String(item.product_id) : undefined,
    variant_id: variantId != null ? String(variantId) : undefined,
    sku: item?.sku || undefined,
    name: productName || undefined,
    variant: item?.variant_title || undefined,
    quantity,
    price: centsToAmount(item?.final_price ?? item?.discounted_price ?? item?.price),
    value: centsToAmount(getLineValueInCents(item)),
    url: item?.url || undefined,
  });
}

function getAddedItems(response, fallbackItems) {
  let items = [];

  if (Array.isArray(response?.items)) {
    items = response.items;
  } else if (response?.id != null) {
    items = [response];
  } else if (Array.isArray(fallbackItems)) {
    items = fallbackItems;
  }

  return items.filter((item) => !isGift(item));
}

/**
 * Capture un ajout panier uniquement après une réponse Shopify réussie.
 * Les cadeaux automatiques sont volontairement exclus de la conversion.
 */
export function captureProductAdded(response, { source, fallbackItems = [], valueInCents, currency } = {}) {
  if (response?.status >= 400 || response?.errors) return;

  const addedItems = getAddedItems(response, fallbackItems);
  if (addedItems.length === 0) return;

  const products = addedItems.map(normalizeItem);
  const quantity = products.reduce((total, item) => total + item.quantity, 0);
  const calculatedValueInCents = addedItems.reduce((total, item) => total + (getLineValueInCents(item) || 0), 0);
  const eventValueInCents = Number.isFinite(Number(valueInCents)) ? Number(valueInCents) : calculatedValueInCents;
  const firstProduct = products[0];

  capture(
    PRODUCT_ADDED_EVENT,
    {
      source: source || 'unknown',
      currency: getCurrency(currency),
      quantity,
      value: centsToAmount(eventValueInCents),
      product_id: products.length === 1 ? firstProduct.product_id : undefined,
      variant_id: products.length === 1 ? firstProduct.variant_id : undefined,
      product_name: products.length === 1 ? firstProduct.name : undefined,
      product_ids: products.map((item) => item.product_id).filter(Boolean),
      variant_ids: products.map((item) => item.variant_id).filter(Boolean),
      products,
    },
    { send_instantly: true },
  );
}

function findCheckoutTrigger(event) {
  const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];

  return eventPath.find(
    (node) => node instanceof Element && node.hasAttribute('data-posthog-checkout-source'),
  );
}

function checkoutPropertiesFromElement(element) {
  const valueInCents = element.dataset.cartValue;
  const itemCount = toInteger(element.dataset.cartItemCount, 0);

  return compact({
    source: element.dataset.posthogCheckoutSource,
    currency: getCurrency(element.dataset.currency),
    value: centsToAmount(valueInCents),
    item_count: itemCount || undefined,
    product_id: element.dataset.productId || undefined,
    variant_id: element.dataset.variantId || undefined,
    product_name: element.dataset.productName || undefined,
  });
}

export function captureCheckoutStarted(trigger) {
  if (!(trigger instanceof Element)) return;

  const properties = checkoutPropertiesFromElement(trigger);
  const fingerprint = [
    window.location.pathname,
    properties.source,
    properties.value,
    properties.item_count,
    properties.product_id,
    properties.variant_id,
  ].join(':');
  const timestamp = Date.now();

  if (
    lastCheckoutCapture.fingerprint === fingerprint &&
    timestamp - lastCheckoutCapture.timestamp < CHECKOUT_DEDUPLICATION_WINDOW_MS
  ) {
    return;
  }

  lastCheckoutCapture = { fingerprint, timestamp };
  capture(CHECKOUT_STARTED_EVENT, properties, { send_instantly: true });
}

document.addEventListener(
  'click',
  (event) => {
    const trigger = findCheckoutTrigger(event);
    if (!trigger || trigger.matches(':disabled') || trigger.getAttribute('aria-disabled') === 'true') return;

    captureCheckoutStarted(trigger);
  },
  true,
);

document.addEventListener(
  'submit',
  (event) => {
    const submitter = event.submitter;
    if (!(submitter instanceof Element)) return;

    const trigger =
      submitter.closest('[data-posthog-checkout-source]') ||
      findCheckoutTrigger({ composedPath: () => [submitter] });

    if (trigger) captureCheckoutStarted(trigger);
  },
  true,
);
